// WP-A · Availability: reads booked date ranges for the villa so the calendar
// can grey-out taken nights + show a dynamic price estimate. The widget MUST
// NOT read the Master sheet or Google Calendar directly from the browser
// (rule #2). It reads a Firestore mirror that the availability Cloud Function
// maintains in `availability/villa`:
//   - booked:       [{start, end}] ISO date ranges (end exclusive)
//   - pricing_band: "$X,XXX" rounded estimate (for the per-night hint)
//   - rate_bands:   [{start, end, rate}] per-date seasonal rates from the
//                   Pricing sheet, so the widget can compute an approximate
//                   total for the selected nights (dynamic, after selection)
//
// Reliability: a localStorage cache (stale-while-revalidate) lets a returning
// visitor see availability instantly while a fresh fetch happens in the
// background. If the background fetch fails, the cached copy is kept.
//
// THE INVARIANT (session 34): without a real data set — no cache AND a failed
// fetch — fetchAvailability THROWS. It must never resolve to an empty booked
// list, because the calendar would render that as "every night free" and let
// a guest pick dates on pure guesswork. The caller (AvailabilityCalendar)
// keeps selection locked and shows an error + retry instead. There is exactly
// one honest "all free" signal: a successful fetch of an empty mirror.
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase.js";

const CACHE_KEY = "obr_availability_v1";
const CACHE_TTL_MS = 1000 * 60 * 60; // 1h: serve cache, refresh in background

/** Read the cached mirror doc from localStorage, or null. */
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist the mirror doc to localStorage (best-effort). */
function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, cached_at: Date.now() }));
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/** Normalise the raw Firestore doc into the shape the calendar consumes. */
function normalize(d) {
  const booked = Array.isArray(d?.booked) ? d.booked : [];
  const pricing_band = typeof d?.pricing_band === "string" ? d.pricing_band : null;
  const rate_bands = Array.isArray(d?.rate_bands) ? d.rate_bands : [];
  return { booked, pricing_band, rate_bands };
}

/**
 * Fetch the availability mirror from Firestore (with a localStorage
 * stale-while-revalidate cache).
 *
 * @param {object} [opts]
 * @param {boolean} [opts.fresh]  true → skip the cache read, force a network
 *                                fetch (used by the refresh button).
 * @returns {Promise<{booked, pricing_band, rate_bands}>}
 * @throws  when there is no cached copy AND the network fetch fails (or the
 *          mirror doc is missing). Callers must treat this as "availability
 *          unknown" — never as "everything free".
 */
export async function fetchAvailability(opts = {}) {
  // 1. Fresh fetch (refresh button, or no cache path).
  if (opts.fresh) {
    const data = await fetchFresh();
    writeCache(data);
    return data;
  }

  // 2. Stale-while-revalidate: serve cache synchronously if present, then
  //    refresh in the background so the next render is current. The caller gets
  //    a resolved promise with the cache (fast) — a separate background refresh
  //    updates the doc for next time.
  const cached = readCache();
  if (cached) {
    // Background refresh (fire-and-forget; updates cache for next load).
    fetchFresh().then(writeCache).catch(() => {});
    return normalize(cached);
  }

  // 3. No cache: the network MUST deliver. A failure throws (the invariant
  //    above) — the calendar stays locked with an error + retry, and never
  //    falls back to a silently-empty "everything free" render.
  const data = await fetchFresh();
  writeCache(data);
  return data;
}

/** One Firestore getDoc, normalised. Throws on failure (caller decides). */
async function fetchFresh() {
  const snap = await getDoc(doc(db, "availability", "villa"));
  if (!snap.exists()) {
    // The mirror doc is written by the availability CF every 15 min. Missing =
    // the pipeline is down, NOT "the villa is free" — treat as a load failure.
    const err = new Error("availability mirror doc missing");
    err.code = "unavailable";
    throw err;
  }
  return normalize(snap.data() || {});
}

/** True if the cached doc is older than TTL (stale → show refresh affordance). */
export function cacheIsStale() {
  const c = readCache();
  if (!c || !c.cached_at) return true;
  return Date.now() - c.cached_at > CACHE_TTL_MS;
}

// --- range + pricing math --------------------------------------------------

// Does [checkin, checkout) overlap any booked range? Ranges are inclusive
// start, exclusive end — so a checkout day that equals a booking's start day is
// allowed (guest-change days are selectable as start/end).
export function isRangeAvailable(booked, checkinISO, checkoutISO) {
  const start = Date.parse(checkinISO + "T00:00:00Z");
  const end = Date.parse(checkoutISO + "T00:00:00Z");
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return false;
  return !booked.some((r) => {
    const b0 = Date.parse(r.start + "T00:00:00Z");
    const b1 = Date.parse(r.end + "T00:00:00Z");
    return start < b1 && b0 < end; // overlap
  });
}

function parseISO(d) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, day));
}

function toISO(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function bandRateForDate(rate_bands, iso) {
  const t = parseISO(iso).getTime();
  for (const b of rate_bands) {
    const s = parseISO(b.start).getTime();
    const e = parseISO(b.end).getTime();
    if (t >= s && t <= e) {
      const r = Number(b.rate);
      if (Number.isFinite(r)) return r;
    }
  }
  return null;
}

function parseBandRate(pricing_band) {
  if (!pricing_band) return null;
  const m = String(pricing_band).replace(/[,,$\s]/g, "");
  const n = Number(m);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Estimate the room-only total for [checkin, checkout) across the seasonal
 * rate_bands. Returns null if no rate info is available. "Before fees" — the
 * actual quote adds cleaning/tax/wedding; this is a transparent ballpark.
 *
 * @returns {{total:number, perNight:number, nights:number, source:'bands'|'band'}|null}
 */
export function estimatePrice(data, checkinISO, checkoutISO) {
  if (!checkinISO || !checkoutISO) return null;
  const ci = parseISO(checkinISO);
  const co = parseISO(checkoutISO);
  if (isNaN(ci.getTime()) || isNaN(co.getTime()) || co <= ci) return null;
  const nights = Math.round((co - ci) / 86400000);
  if (nights <= 0) return null;

  // 1. Per-date seasonal bands (most accurate).
  if (data?.rate_bands?.length) {
    let total = 0;
    let covered = 0;
    const cursor = new Date(ci);
    while (cursor < co) {
      const r = bandRateForDate(data.rate_bands, toISO(cursor));
      if (r != null) {
        total += r;
        covered++;
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    if (covered === nights) {
      return { total: Math.round(total), perNight: Math.round(total / nights), nights, source: "bands" };
    }
    if (covered > 0) {
      // Partial coverage: extrapolate the average across uncovered nights.
      const avg = total / covered;
      total = Math.round(total + avg * (nights - covered));
      return { total, perNight: Math.round(total / nights), nights, source: "bands" };
    }
  }

  // 2. Fallback: single pricing_band × nights.
  const bandRate = parseBandRate(data?.pricing_band);
  if (bandRate != null) {
    return { total: bandRate * nights, perNight: bandRate, nights, source: "band" };
  }
  return null;
}
